import { api } from './api';

export interface InventoryItem {
  productId: string;
  totalQuantity: number;
  totalReserved: number;
  totalStock: number;
  totalValue: number;
  locationsCount: number;
  isLowStock: boolean;
  locations: {
    locationId: string;
    locationName: string;
    excelFile: string;
    quantity: number;
    reserved: number;
    totalQuantity: number;
    lastUpdated: string;
  }[];
  product?: {
    id: string;
    name: string;
    sku: string;
    code: string;
    barcode: string;
    category: string;
    unit: string;
    costPrice: number;
    salePrice: number;
    minStock: number;
  };
}

export interface InventoryResponse {
  success: boolean;
  data: {
    inventory: InventoryItem[];
    statistics: {
      totalProducts: number;
      totalStock: number;
      totalValue: number;
      lowStockCount: number;
      averageLocationsPerProduct: number;
    };
  };
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export const inventoryApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getInventories: builder.query<InventoryResponse, {
      page?: number;
      limit?: number;
      search?: string;
      locationId?: string;
      lowStock?: boolean;
    }>({
      query: (params = {}) => {
        const searchParams = new URLSearchParams();
        
        if (params.page) searchParams.append('page', params.page.toString());
        if (params.limit) searchParams.append('limit', params.limit.toString());
        if (params.search) searchParams.append('search', params.search);
        if (params.locationId) searchParams.append('locationId', params.locationId);
        if (params.lowStock) searchParams.append('lowStock', params.lowStock.toString());
        
        return {
          url: `/inventory/unified?${searchParams.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['Inventory'],
    }),
    
    adjustInventory: builder.mutation<{ success: boolean }, {
      productId: string;
      locationId: string;
      type: 'SET' | 'ADD' | 'SUBTRACT';
      quantity: number;
      reason: string;
    }>({
      query: (adjustmentData) => ({
        url: '/inventory/adjust',
        method: 'PUT',
        body: adjustmentData,
      }),
      invalidatesTags: ['Inventory'],
    }),

    transferInventory: builder.mutation<{ success: boolean; message: string }, {
      productId: string;
      fromLocationId: string;
      toLocationId: string;
      quantity: number;
      reason?: string;
    }>({
      query: (transferData) => ({
        url: '/inventory/transfer',
        method: 'POST',
        body: transferData,
      }),
      invalidatesTags: ['Inventory'],
    }),

    getInventoryAdjustmentHistory: builder.query<{
      success: boolean;
      data: Array<{
        id: string;
        date: string;
        user: {
          id: string;
          name: string;
          email: string;
        };
        location: {
          id: string;
          name: string;
        } | null;
        productId: string;
        details: {
          productName: string;
          type: 'SET' | 'ADD' | 'SUBTRACT';
          previousQuantity: number;
          newQuantity: number;
          quantity: number;
          reason: string;
        };
        metadata: any;
      }>;
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
        hasNext: boolean;
        hasPrev: boolean;
      };
    }, {
      page?: number;
      limit?: number;
      locationId?: string;
      productId?: string;
      userId?: string;
      startDate?: string;
      endDate?: string;
    }>({
      query: (params = {}) => {
        const searchParams = new URLSearchParams();
        
        if (params.page) searchParams.append('page', params.page.toString());
        if (params.limit) searchParams.append('limit', params.limit.toString());
        if (params.locationId) searchParams.append('locationId', params.locationId);
        if (params.productId) searchParams.append('productId', params.productId);
        if (params.userId) searchParams.append('userId', params.userId);
        if (params.startDate) searchParams.append('startDate', params.startDate);
        if (params.endDate) searchParams.append('endDate', params.endDate);
        
        return {
          url: `/inventory/adjustments/history?${searchParams.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['Inventory'],
    }),

    bulkAdjustInventory: builder.mutation<{
      success: boolean;
      data: {
        bulkAdjustmentCode: string;
        successfulAdjustments: number;
        failedAdjustments: number;
        totalAdjustments: number;
        results: Array<{
          index: number;
          productId: string;
          locationId: string;
          productName: string;
          locationName: string;
          previousQuantity: number;
          newQuantity: number;
          success: boolean;
        }>;
        errors: Array<{
          index: number;
          productId: string;
          locationId?: string;
          error: string;
          currentQuantity?: number;
          requestedQuantity?: number;
        }>;
      };
      message: string;
    }, {
      adjustments: Array<{
        productId: string;
        locationId: string;
        type: 'SET' | 'ADD' | 'SUBTRACT';
        quantity: number;
      }>;
      reason?: string;
    }>({
      query: (adjustmentData) => ({
        url: '/inventory/bulk-adjust',
        method: 'PUT',
        body: adjustmentData,
      }),
      invalidatesTags: ['Inventory', 'BulkAdjustments'],
    }),

    getBulkAdjustmentHistory: builder.query<{
      success: boolean;
      data: Array<{
        id: string;
        code: string;
        date: string;
        user: {
          id: string;
          name: string;
          email: string;
        };
        reason: string | null;
        totalAdjustments: number;
        successfulAdjustments: number;
        failedAdjustments: number;
        status: string;
        items: Array<{
          id: string;
          product: {
            id: string;
            name: string;
            code: string;
          };
          location: {
            id: string;
            name: string;
          };
          type: string;
          quantity: number;
          previousQuantity: number;
          newQuantity: number;
          success: boolean;
          error: string | null;
        }>;
      }>;
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
        hasNext: boolean;
        hasPrev: boolean;
      };
    }, {
      page?: number;
      limit?: number;
      userId?: string;
      startDate?: string;
      endDate?: string;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
    }>({
      query: (params = {}) => {
        const searchParams = new URLSearchParams();
        
        if (params.page) searchParams.append('page', params.page.toString());
        if (params.limit) searchParams.append('limit', params.limit.toString());
        if (params.userId) searchParams.append('userId', params.userId);
        if (params.startDate) searchParams.append('startDate', params.startDate);
        if (params.endDate) searchParams.append('endDate', params.endDate);
        if (params.sortBy) searchParams.append('sortBy', params.sortBy);
        if (params.sortOrder) searchParams.append('sortOrder', params.sortOrder);
        
        return {
          url: `/inventory/bulk-adjustments/history?${searchParams.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['BulkAdjustments'],
    }),

    getBulkAdjustmentDetails: builder.query<{
      success: boolean;
      data: {
        id: string;
        code: string;
        createdAt: string;
        updatedAt: string;
        reason: string | null;
        totalAdjustments: number;
        successfulAdjustments: number;
        failedAdjustments: number;
        status: string;
        user: {
          id: string;
          name: string;
          email: string;
        };
        items: Array<{
          id: string;
          product: {
            id: string;
            name: string;
            code: string;
            sku: string;
            category: string;
            unit: string;
          };
          location: {
            id: string;
            name: string;
          };
          type: string;
          quantity: number;
          previousQuantity: number;
          newQuantity: number;
          success: boolean;
          error: string | null;
          createdAt: string;
        }>;
        statistics: {
          totalItems: number;
          successfulItems: number;
          failedItems: number;
          totalQuantityChanged: number;
          locationsCovered: number;
        };
      };
    }, string>({
      query: (code) => ({
        url: `/inventory/bulk-adjustments/${code}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, code) => [{ type: 'BulkAdjustments', id: code }],
    }),
  }),
});

export const {
  useGetInventoriesQuery,
  useAdjustInventoryMutation,
  useTransferInventoryMutation,
  useGetInventoryAdjustmentHistoryQuery,
  useBulkAdjustInventoryMutation,
  useGetBulkAdjustmentHistoryQuery,
  useGetBulkAdjustmentDetailsQuery,
} = inventoryApi;